import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { logger } from "../lib/logger";
import {
  pushSupported,
  pushPermission,
  pushIsSubscribed,
  subscribePush,
  unsubscribePush,
  sendTestPush,
} from "../lib/push";

export default function NotificationToggle({ compact = false }) {
  const [supported] = useState(pushSupported());
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState(pushPermission());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    pushIsSubscribed().then(setSubscribed);
  }, [supported]);

  if (!supported) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      setSubscribed(true);
      setPermission("granted");
      toast.success("Notifications activées ✓");
      // Petit ping de test discret pour confirmer côté serveur — best-effort, on ignore l'erreur
      try { await sendTestPush(); } catch (testErr) { logger.debug("test push failed (non-bloquant):", testErr); }
    } catch (e) {
      toast.error(e.message || "Activation refusée");
      setPermission(pushPermission());
    } finally {
      setBusy(false);
    }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      setSubscribed(false);
      toast.success("Notifications désactivées");
    } catch (e) {
      toast.error(e.message || "Erreur lors du désabonnement");
    } finally {
      setBusy(false);
    }
  };

  // Bloqué au niveau du navigateur
  if (permission === "denied") {
    return (
      <Button
        size={compact ? "sm" : "default"}
        disabled
        variant="outline"
        className={`rounded-full ${compact ? "h-9 px-3" : "h-11 px-4"} border-[#FBE5E4] text-[#B0413E] cursor-not-allowed`}
        title="Notifications bloquées par votre navigateur. Modifiez les réglages du site pour les autoriser."
        data-testid="notif-toggle-blocked"
      >
        <BellOff className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} mr-1.5`} strokeWidth={1.5} />
        Notifications bloquées
      </Button>
    );
  }

  if (subscribed) {
    return (
      <Button
        size={compact ? "sm" : "default"}
        onClick={disable}
        disabled={busy}
        variant="outline"
        className={`rounded-full ${compact ? "h-9 px-3" : "h-11 px-4"} border-[#1E7A4D] text-[#1E7A4D] hover:bg-[#E0F2E9]`}
        data-testid="notif-toggle-on"
        title="Cliquer pour désactiver les notifications"
      >
        <BellRing className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} mr-1.5`} strokeWidth={1.5} />
        Notifications activées
      </Button>
    );
  }

  return (
    <Button
      size={compact ? "sm" : "default"}
      onClick={enable}
      disabled={busy}
      variant="outline"
      className={`rounded-full ${compact ? "h-9 px-3" : "h-11 px-4"} border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#E0E7FF]`}
      data-testid="notif-toggle-off"
      title="Recevoir des notifications pour les actualités importantes"
    >
      <Bell className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} mr-1.5`} strokeWidth={1.5} />
      {busy ? "Activation…" : "Activer les notifications"}
    </Button>
  );
}
